import React, { useState, useEffect } from 'react';
import { message, Spin, Space, Button } from 'antd';
import axios from 'axios';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import { useParams } from 'react-router-dom';
import '../pages/Users.css';

const OrgUserTab = () => {
  const { orgId } = useParams();
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPasswordModal, setIsPasswordModal] = useState(false);

  useEffect(() => {
    fetchUsers();
    if (!orgId) fetchOrganizations();
  }, [orgId, pagination.current, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/organizations/${orgId}/users`,
        {
          params: {
            page: pagination.current,
            limit: pagination.pageSize,
            search: searchTerm,
          },
        }
      );
      const { users, meta } = response.data;
      setUsersData(formatUsersData(users));
      setPagination((prev) => ({
        ...prev,
        total: meta.total,
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      message.error('Failed to load organizations');
    }
  };

  const handleCreateUser = async (formData) => {
    try {
      const organizationId = orgId || formData.organizationId;
      await axios.post(
        `${process.env.REACT_APP_API}/api/auth/create-user`,
        { name: formData.name, email: formData.email, password: formData.password },
        { params: { organizationId } }
      );
      message.success('User created successfully');
      fetchUsers();
      setModalVisible(false);
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user');
    }
  };

  const handleChangePassword = async (userId, newPassword) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/api/auth/change-password/${userId}`,
        { newPassword }
      );
      message.success('Password changed successfully');
      setModalVisible(false);
    } catch (error) {
      console.error('Error changing password:', error);
      message.error('Failed to change password. Please try again later.');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await axios.patch(
        `${process.env.REACT_APP_API}/api/organizations/users/${userId}/toggle-status`,
        {}
      );
      message.success(`User status changed to ${newStatus}`);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      message.error('Failed to toggle user status');
    }
  };

  const formatUsersData = (data) => {
    return data.map((user) => ({
      ...user,
      verified: user.verified ? 'Yes' : 'No',
    }));
  };

  const handleTableChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div>
      <div className="userTab-page">
        <Space
          style={{
            marginBottom: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2>Users across Organizations</h2>
          <Button
            type="primary"
            onClick={() => {
              setIsPasswordModal(false);
              setModalVisible(true);
            }}
          >
            Create User
          </Button>
        </Space>

        <GenericTable
          data={usersData}
          searchKeys={['userName', 'email']}
          sortableColumns={['userName', 'email', 'verified', 'status', 'role']}
          actions={['Toggle Status', 'Change Password']}
          actionFunctions={[
            (record) => toggleUserStatus(record.userId, record.status),
            (record) => {
              setSelectedUser(record);
              setIsPasswordModal(true);
              setModalVisible(true);
            },
          ]}

          searchInput={searchTerm}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: handleTableChange,
          }}
          onSearch={handleSearch}
        />

        <GenericModal
          isVisible={modalVisible}
          handleOk={(formData) => {
            if (isPasswordModal) {
              handleChangePassword(selectedUser.userId, formData.newPassword);
            } else {
              handleCreateUser(formData);
            }
          }}
          handleCancel={() => setModalVisible(false)}
          title={isPasswordModal ? 'Change User Password' : 'Create New User'}
          inputs={
            isPasswordModal
              ? [{ name: 'newPassword', label: 'New Password', type: 'password', required: true }]
              : [
                  { name: 'name', label: 'Name', type: 'text', required: true },
                  { name: 'email', label: 'Email', type: 'email', required: true },
                  { name: 'password', label: 'Password', type: 'password', required: true },
                  ...(orgId
                    ? []
                    : [
                        {
                          label: 'Organization',
                          name: 'organizationId',
                          type: 'select',
                          options: organizations.map((org) => ({ value: org.id, label: org.name })),
                          placeholder: 'Select Organization',
                          required: true,
                        },
                      ]),
                ]
          }
        />
      </div>
    </div>
  );
};

export default OrgUserTab;
